<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import { useI18n } from 'vue-i18n';
import { DomainInputRequestInput } from '@/types/api/User/DomainInputRequest';
import AdminDomainForm from './AdminDomainForm.vue';
import { createDomain, getAllSuppliers, SupplierForAdmin } from '@/services/api/userService';
import { getAllPackages } from '@/services/api/productService';
import { MinimalPackageDto } from '@/types/api/Product/MinimalPackageDto';

const { t } = useI18n();
const router = useRouter();

const domain = ref<DomainInputRequestInput>({
  price: 0,
  logo: null,
  urlSuffix: '',
  companyWebsite: '',
  released: false,
  supplierId: null,
  colorSchemas: [
    { domainId: null, schemaType: 'COLOR', hexCode: '#000000' },
    { domainId: null, schemaType: 'TEXT', hexCode: '#FFFFFF' }
  ],
  packagesIds: []
});

const suppliers = ref<Array<SupplierForAdmin>>([]);
const packages = ref<Array<MinimalPackageDto>>([]);
const loading = ref(false);
const loadingMeta = ref(true);

async function loadMeta() {
  loadingMeta.value = true;
  try {
    const [sup, pack] = await Promise.all([getAllSuppliers(), getAllPackages()]);
    suppliers.value = sup ?? [];
    packages.value = pack ?? [];
  } finally {
    loadingMeta.value = false;
  }
}

async function onSubmit(payload: DomainInputRequestInput) {
  loading.value = true;
  try {
    await createDomain(payload);
    ElMessage.success(t('components.adminDashboard.createDomain.page.toastSuccessCreated'));
    router.push('/admin/domains');
  } catch (e: any) {
    ElMessage.error(
      e?.response?.data?.message
        ?? t('components.adminDashboard.createDomain.page.toastErrorCreated')
    );
  } finally {
    loading.value = false;
  }
}

onMounted(loadMeta);
</script>

<template>
  <div class="page">
    <h2 class="title">
      {{ $t('components.adminDashboard.createDomain.page.title') }}
    </h2>

    <el-card>
      <template #header>
        <div class="card-header">
          {{ $t('components.adminDashboard.createDomain.page.cardHeaderNew') }}
        </div>
      </template>

      <el-skeleton v-if="loadingMeta" :rows="6" animated />

      <AdminDomainForm
        v-else-if="domain"
        v-model="domain"
        :suppliers="suppliers"
        :packages="packages"
        :loading="loading"
        @submit="onSubmit"
      />
    </el-card>
  </div>
</template>

<style scoped>
.page { max-width: 920px; margin: 24px auto; padding: 0 16px; }
.title { margin: 0 0 12px; }
.card-header { font-weight: 600; }
</style>
